import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Building2, CreditCard, Wallet, Loader2, DollarSign, Briefcase, MoreHorizontal } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import { useAuthStore } from '@/store/authStore';
import { useAccountsStore } from '@/store/accountsStore';
import { useCurrenciesStore } from '@/store/currenciesStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useTheme } from '@/components/ThemeProvider';

const accountTypes = [
  { id: 'checking', icon: Building2, color: '#2962FF' },
  { id: 'savings', icon: Building2, color: '#2962FF' },
  { id: 'credit', icon: CreditCard, color: '#2962FF' },
  { id: 'cash', icon: Wallet, color: '#2962FF' },
  { id: 'investment', icon: Briefcase, color: '#2962FF' },
  { id: 'loan', icon: Building2, color: '#2962FF' },
  { id: 'other', icon: MoreHorizontal, color: '#2962FF' },
] as const;

const formSchema = z.object({
  name: z.string().min(1, 'Account name is required'),
  type: z.enum(['checking', 'savings', 'credit', 'cash', 'investment', 'loan', 'other']),
  balance: z.coerce.number(),
  currency: z.string().min(1, 'Currency is required'),
  limit: z.coerce.number().optional(),
  notes: z.string().optional(),
  isDefault: z.boolean().default(false),
  isArchived: z.boolean().optional(),
  color: z.string().default('#2962FF'),
  icon: z.string().default('wallet'),
});

type FormData = z.infer<typeof formSchema>;

interface AddAccountProps {
  open: boolean;
  onClose: () => void;
}

export default function AddAccount({ open, onClose }: AddAccountProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedColor, setSelectedColor] = useState(theme === 'dark' ? '#4CAF50' : '#1E40AF');
  
  // Update color when theme changes
  useEffect(() => {
    setSelectedColor(theme === 'dark' ? '#4CAF50' : '#1E40AF');
  }, [theme]);
  
  const [showColorPicker, setShowColorPicker] = useState(false);
  const { user } = useAuthStore();
  const { addAccount } = useAccountsStore();
  const { currencies } = useCurrenciesStore();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      type: 'checking',
      balance: 0,
      currency: currencies.find(c => c.isDefault)?.code || '',
      limit: undefined,
      notes: '',
      isDefault: false,
      isArchived: false,
      color: selectedColor,
      icon: 'wallet',
    },
  });

  const selectedType = form.watch('type');

  const handleSubmit = async (data: z.infer<typeof formSchema>) => {
    if (!user?.id) {
      toast.error(t('common.error'), {
        description: t('accounts.messages.mustBeLoggedIn')
      });
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Find the currency object
      const currency = currencies.find(c => c.code === data.currency);
      
      if (!currency) {
        toast.error(t('common.error'), {
          description: t('accounts.messages.currencyNotFound')
        });
        setIsSubmitting(false);
        return;
      }
      
      // Prepare account data - isArchived and isDefault will be handled in the core store
      const accountData = {
        name: data.name,
        type: data.type,
        balance: parseFloat(data.balance.toString()),
        currency: data.currency,
        color: data.color,
        icon: data.icon,
        isDefault: data.isDefault,
        // Remove notes field to avoid schema cache issues
      };
      
      // Try to add the account
      let accountId: string | null = null;
      try {
        accountId = await addAccount(accountData);
      } catch (innerError) {
        // Check if it's a schema cache error
        const errorMessage = innerError instanceof Error ? innerError.message : String(innerError);
        if (errorMessage.includes('schema cache')) {
          toast.error('Database schema cache error', {
            description: 'Please try again with fewer fields.'
          });
        } else {
          throw innerError; // Re-throw to be caught by the outer catch
        }
        return;
      }
      
      if (accountId) {
        toast.success(t('accounts.messages.addSuccess'), {
          description: t('accounts.messages.addSuccessDescription')
        });
        
        form.reset();
        onClose();
      } else {
        toast.error(t('common.error'), {
          description: t('accounts.messages.addError')
        });
      }
    } catch (error) {
      toast.error(t('common.error'), {
        description: t('accounts.messages.addError')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="block sm:max-w-[420px] w-full mx-auto p-0 sm:rounded-2xl overflow-hidden bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-0 sm:border sm:border-border sm:dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 shadow-lg">
        <div className="relative">
          <div className="p-5">
            <DialogHeader className="space-y-2 mb-5">
              <DialogTitle className="text-xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
                {t('accounts.addAccount')}
              </DialogTitle>
              <DialogDescription className="text-sm text-text-secondary dark:text-text-dark-secondary">
                {t('accounts.addAccountDescription')}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              {/* Account Type */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  {t('accounts.fields.type')}
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  {accountTypes.map(({ id, icon: Icon }) => (
                    <Button
                      key={id}
                      type="button"
                      variant={selectedType === id ? 'default' : 'outline'}
                      className={cn(
                        "flex flex-col items-center justify-center py-2.5 h-auto space-y-1.5 transition-all duration-200",
                        selectedType === id 
                          ? "bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white shadow-sm" 
                          : "bg-background-secondary/50 dark:bg-background-dark-secondary/50 border-border dark:border-border-dark hover:border-secondary dark:hover:border-primary-dark text-text-primary dark:text-text-dark-primary hover:bg-border/5 dark:hover:bg-border-dark/5"
                      )}
                      onClick={() => form.setValue('type', id as 'checking' | 'savings' | 'credit' | 'cash' | 'investment' | 'loan' | 'other')}
                    >
                      <Icon className={cn(
                        "w-5 h-5 transition-colors",
                        selectedType === id 
                          ? "text-white" 
                          : "text-secondary dark:text-primary-dark"
                      )} />
                      <span className="text-xs font-medium">{t(`accounts.types.${id}`)}</span>
                    </Button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Account Name */}
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="name" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    {t('accounts.fields.name')}
                  </Label>
                  <Input
                    id="name"
                    {...form.register('name')}
                    className="h-10 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark focus:ring-1 focus:ring-secondary dark:focus:ring-primary-dark rounded-lg"
                    placeholder={t('accounts.placeholders.name')}
                  />
                  {form.formState.errors.name && (
                    <p className="text-xs text-error mt-1">{form.formState.errors.name.message}</p>
                  )}
                </div>

                {/* Initial Balance */}
                <div className="space-y-1.5">
                  <Label htmlFor="balance" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    {t('accounts.fields.balance')}
                  </Label>
                  <div className="relative">
                    <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary dark:text-primary-dark" />
                    <Input
                      id="balance"
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      {...form.register('balance')}
                      className="pl-8 h-10 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark focus:ring-1 focus:ring-secondary dark:focus:ring-primary-dark rounded-lg"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Currency */}
                <div className="space-y-1.5">
                  <Label htmlFor="currency" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    {t('accounts.form.currency')}
                  </Label>
                  <Select
                    value={form.watch('currency')}
                    onValueChange={(value) => form.setValue('currency', value)}
                  >
                    <SelectTrigger className="h-10 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark focus:ring-1 focus:ring-secondary dark:focus:ring-primary-dark rounded-lg">
                      <SelectValue placeholder={t('accounts.form.selectCurrency')} />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((currency) => (
                        <SelectItem 
                          key={currency.code} 
                          value={currency.code}
                          className="focus:bg-secondary/10 dark:focus:bg-primary-dark/10 focus:text-secondary dark:focus:text-primary-dark"
                        >
                          {currency.code} - {currency.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Credit Limit - Only for credit type */}
                {selectedType === 'credit' && (
                  <div className="col-span-2 space-y-1.5">
                    <Label htmlFor="limit" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                      {t('accounts.fields.creditLimit')}
                    </Label>
                    <div className="relative">
                      <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary dark:text-primary-dark" />
                      <Input
                        id="limit"
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        {...form.register('limit')}
                        className="pl-8 h-10 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark focus:ring-1 focus:ring-secondary dark:focus:ring-primary-dark rounded-lg"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                )}

                {/* Account Color */}
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    {t('accounts.fields.color')}
                  </Label>
                  <Popover open={showColorPicker} onOpenChange={setShowColorPicker}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-10 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border-border dark:border-border-dark hover:border-secondary dark:hover:border-primary-dark hover:bg-border/5 dark:hover:bg-border-dark/5 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-4 h-4 rounded-full ring-1 ring-border dark:ring-border-dark"
                            style={{ backgroundColor: selectedColor }}
                          />
                          <span className="text-sm text-text-primary dark:text-text-dark-primary">
                            {selectedColor}
                          </span>
                        </div>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-3 bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150">
                      <HexColorPicker color={selectedColor} onChange={setSelectedColor} />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Default Account */}
                <div className="col-span-2 flex items-center space-x-2">
                  <Checkbox
                    id="isDefault"
                    checked={form.watch('isDefault')}
                    onCheckedChange={(checked) => form.setValue('isDefault', checked as boolean)}
                    className="h-4 w-4 rounded border-border dark:border-border-dark data-[state=checked]:bg-secondary data-[state=checked]:border-secondary dark:data-[state=checked]:bg-primary-dark dark:data-[state=checked]:border-primary-dark"
                  />
                  <Label
                    htmlFor="isDefault"
                    className="text-sm text-text-primary dark:text-text-dark-primary cursor-pointer select-none"
                  >
                    {t('accounts.fields.isDefault')}
                  </Label>
                </div>

                {/* Notes */}
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="notes" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    {t('accounts.fields.notes')}
                  </Label>
                  <Input
                    id="notes"
                    {...form.register('notes')}
                    className="h-10 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark focus:ring-1 focus:ring-secondary dark:focus:ring-primary-dark rounded-lg"
                    placeholder={t('accounts.placeholders.notes')}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="w-full sm:w-1/2 h-10 bg-background-secondary/80 dark:bg-background-dark-secondary/80 text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 hover:text-secondary dark:hover:text-primary-dark border-secondary dark:border-primary-dark backdrop-blur-xl backdrop-saturate-150 rounded-lg"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-1/2 h-10 bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white shadow-sm transition-all duration-200 rounded-lg"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('accounts.addAccount')
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}